import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Mic, Pause, Play, Square } from '../icons'

import { api } from '../api'

const MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
]

function pickMimeType() {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') return ''
  return MIME_TYPES.find((type) => MediaRecorder.isTypeSupported?.(type)) || ''
}

function buildButtonClass(active, compact) {
  if (compact) {
    return `inline-flex h-10 w-10 items-center justify-center rounded-full transition ${active ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-900 hover:text-white'}`
  }

  return `inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${active ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-900 hover:text-white'}`
}

export default function AudioRecorderButton({ onTranscript, onStatusChange, disabled = false, compact = false, className = '' }) {
  // Mic feature temporarily disabled
  const isMicDisabled = true;

  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])

  const mimeType = useMemo(() => pickMimeType(), [])

  useEffect(() => {
    onStatusChange?.(status)
  }, [status, onStatusChange])

  useEffect(() => () => {
    try {
      recorderRef.current?.stop?.()
    } catch {}
    streamRef.current?.getTracks?.().forEach((track) => track.stop())
  }, [])

  async function startRecording() {
    if (disabled || isMicDisabled || status === 'transcribing') return
    setError('')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      chunksRef.current = []
      streamRef.current = stream
      recorderRef.current = recorder

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data)
      }

      recorder.onstop = async () => {
        const blobType = mimeType || recorder.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: blobType })
        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        recorderRef.current = null
        chunksRef.current = []

        if (!blob.size) {
          setStatus('idle')
          setError('No audio captured. Please try again.')
          return
        }

        setStatus('transcribing')
        try {
          const extension = blobType.includes('mp4') ? 'm4a' : 'webm'
          const response = await api.transcribeSpeech(blob, `speech-input.${extension}`)
          const transcript = (response?.transcript || '').trim()
          if (!transcript) throw new Error('No speech detected in the recording.')
          onTranscript?.(transcript, response)
          setStatus('idle')
        } catch (err) {
          setStatus('idle')
          setError(err?.message || 'Speech transcription failed.')
        }
      }

      recorder.start()
      setStatus('recording')
    } catch (err) {
      setStatus('idle')
      setError(err?.message || 'Microphone access was denied.')
    }
  }

  function pauseOrResume() {
    const recorder = recorderRef.current
    if (!recorder) return
    if (status === 'recording') {
      recorder.pause()
      setStatus('paused')
      return
    }
    if (status === 'paused') {
      recorder.resume()
      setStatus('recording')
    }
  }

  function stopRecording() {
    const recorder = recorderRef.current
    if (!recorder) return
    if (recorder.state !== 'inactive') recorder.stop()
  }

  const isBusy = disabled || isMicDisabled || status === 'transcribing'
  const active = status === 'recording' || status === 'paused'

  return (
    <div className={`flex flex-col items-end gap-1 ${className}`.trim()}>
      <div className="flex items-center gap-2">
        {active ? (
          <>
            <button
              type="button"
              onClick={pauseOrResume}
              disabled={isBusy}
              className={buildButtonClass(true, compact)}
              title={status === 'paused' ? 'Resume recording' : 'Pause recording'}
            >
              {status === 'paused' ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              {compact ? null : <span>{status === 'paused' ? 'Resume' : 'Pause'}</span>}
            </button>
            <button
              type="button"
              onClick={stopRecording}
              disabled={isBusy}
              className={buildButtonClass(true, compact)}
              title="Stop recording"
            >
              <Square className="h-4 w-4" />
              {compact ? null : <span>Stop</span>}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            disabled={isBusy}
            className={buildButtonClass(false, compact)}
            title="Record speech"
          >
            {status === 'transcribing' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
            {compact ? null : <span>{status === 'transcribing' ? 'Converting…' : 'Mic'}</span>}
          </button>
        )}
      </div>
      {status === 'recording' ? <div className="text-[11px] text-rose-600">Recording… click pause or stop</div> : null}
      {status === 'paused' ? <div className="text-[11px] text-amber-600">Recording paused</div> : null}
      {status === 'transcribing' ? <div className="text-[11px] text-slate-500">Converting speech to English…</div> : null}
      {error ? <div className="max-w-[240px] text-right text-[11px] text-rose-600">{error}</div> : null}
    </div>
  )
}
